import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, ArrowRight, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, ReadinessBar, FrameworkBadge } from "@/components/common/bits";
import { ThemeToggle } from "@/components/layout/AppShell";
import { PlanBadge } from "@/components/common/PlanBadge";
import { GeniePanel } from "@/components/common/GeniePanel";
import { Sparkline } from "@/components/common/Sparkline";
import { Prospects } from "@/pages/Prospects";

export default function Consultant() {
  const { session, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const { data: clients, reload } = useApi("/consultant/clients");
  const { data: scoping } = useApi("/scoping/questions");
  const [addOpen, setAddOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sector: "it_services", sector_template: "it_services" });
  const [clone, setClone] = useState({ source_org_id: "", target_org_ids: [], include_controls: false });

  const addClient = async () => {
    try { await api.post("/orgs", form); toast.success("Client organisation created with sector template"); setAddOpen(false); await refresh(); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runClone = async () => {
    try {
      const r = await api.post("/consultant/clone", clone);
      const total = r.data.results.reduce((a, x) => a + x.policies_cloned, 0);
      toast.success(`Cloned ${total} policies across ${r.data.results.length} client(s)`);
      setCloneOpen(false); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen">
      <header className="h-16 border-b flex items-center justify-between px-6 lg:px-10 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 sticky top-0 z-20">
        <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-extrabold">C</div><span className="font-display font-bold text-lg">ComplyKosh</span><span className="eyebrow ml-3 hidden sm:inline">Consultant workspace</span></div>
        <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground hidden sm:inline" data-testid="consultant-user">{session.user.name}</span><PlanBadge />{session.user.is_platform_admin && <Button variant="ghost" size="sm" asChild data-testid="admin-link"><Link to="/admin" className="text-xs">Admin</Link></Button>}<ThemeToggle /><Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn"><LogOut size={16} /></Button></div>
      </header>
      <main className="px-6 lg:px-10 py-8 max-w-[1400px]">
        <PageHeader eyebrow="All clients" title="Client organisations" subtitle="Track readiness across every client, add tenants from sector templates, and clone policy sets in one action."
          testId="consultant-header"
          actions={<><Button variant="outline" onClick={() => setCloneOpen(true)} data-testid="open-clone-btn"><Copy size={15} className="mr-1.5" /> Clone policy set</Button><Button onClick={() => setAddOpen(true)} data-testid="add-client-btn"><Plus size={15} className="mr-1.5" /> Add client</Button></>} />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="client-grid">
          {(clients || []).map((c, i) => (
            <div key={c.id} className="surface p-5 flex flex-col gap-4 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div><div className="eyebrow">{c.sector?.replace("_", " ")}</div><div className="font-display font-semibold text-base mt-1">{c.name}</div></div>
                <div className="text-right"><div className="font-display text-3xl font-bold tabular-nums" data-testid={`client-readiness-${c.id}`}>{c.overall_percent}%</div><div className="text-[10px] text-muted-foreground -mt-1">readiness</div></div>
              </div>
              <div className="flex items-center justify-between -mt-1"><span className="eyebrow">12-week trend</span><Sparkline points={c.history || []} width={120} height={30} testId={`client-sparkline-${c.id}`} /></div>
              <div className="space-y-2">
                {c.readiness.length === 0 && <div className="text-xs text-muted-foreground">No scoping yet — run the wizard.</div>}
                {c.readiness.map((r) => <div key={r.code}><div className="flex justify-between text-xs mb-1"><FrameworkBadge code={r.code} /><span className="tabular-nums text-muted-foreground">{r.implemented}/{r.applicable} · {r.percent}%</span></div><ReadinessBar percent={r.percent} color={r.color} /></div>)}
              </div>
              <div className="grid grid-cols-4 gap-2 text-center text-xs border-t pt-3">
                {[["Open NCs", c.open_nc], ["Tasks", c.open_tasks], ["Policies", c.policies], ["Members", c.members]].map(([l, v]) => <div key={l}><div className="font-semibold tabular-nums">{v}</div><div className="text-muted-foreground">{l}</div></div>)}
              </div>
              <Button variant="secondary" className="justify-between" onClick={() => navigate(`/org/${c.id}/dashboard`)} data-testid={`open-client-${c.id}`}>Open workspace <ArrowRight size={15} /></Button>
            </div>
          ))}
        </div>
        <Prospects scoping={scoping} onConverted={async () => { await refresh(); reload(); }} />
      </main>
      <GeniePanel orgId={null} canManage={false} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-client-dialog" description="Create a new client organisation in your workspace.">
          <DialogHeader><DialogTitle>Add client organisation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Organisation name"><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Sector template" hint="Applies scoping, frameworks and applicable controls instantly. Refine later in the wizard.">
              <Select value={form.sector_template} onValueChange={(v) => setForm({ ...form, sector_template: v, sector: v })}>
                <SelectTrigger data-testid="client-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(scoping?.sector_templates || []).map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter><Button onClick={addClient} disabled={!form.name} data-testid="client-create-submit">Create client</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent data-testid="clone-dialog" description="Copy adopted policies from one client to others.">
          <DialogHeader><DialogTitle>Clone policy set across clients</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Source organisation">
              <Select value={clone.source_org_id} onValueChange={(v) => setClone({ ...clone, source_org_id: v, target_org_ids: clone.target_org_ids.filter((t) => t !== v) })}>
                <SelectTrigger data-testid="clone-source-select"><SelectValue placeholder="Choose source" /></SelectTrigger>
                <SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.policies} policies)</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Target organisations">
              <div className="space-y-2 border rounded-md p-3">
                {(clients || []).filter((c) => c.id !== clone.source_org_id).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid={`clone-target-${c.id}`} checked={clone.target_org_ids.includes(c.id)} onCheckedChange={(v) => setClone({ ...clone, target_org_ids: v ? [...clone.target_org_ids, c.id] : clone.target_org_ids.filter((t) => t !== c.id) })} />
                    {c.name}
                  </label>
                ))}
              </div>
            </Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid="clone-include-controls" checked={clone.include_controls} onCheckedChange={(v) => setClone({ ...clone, include_controls: !!v })} /> Also copy control scope (SoA) to targets missing it</label>
          </div>
          <DialogFooter><Button onClick={runClone} disabled={!clone.source_org_id || !clone.target_org_ids.length} data-testid="clone-submit">Clone now</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
